import type { FC } from 'react';
import React from 'react';
import { defineMessages, useIntl } from 'react-intl-next';
import { graphql } from 'react-relay';
// We have deprecated unstated. Please use react-sweet-state instead
// eslint-disable-next-line no-restricted-imports
import { Subscribe } from 'unstated';

import { MutationInterop as Mutation } from '@confluence/apollo-relay-interop/entry-points/MutationInterop.ts';
import { AtlassianSupportLink } from '@confluence/error-boundary/entry-points/AtlassianSupportLink.ts';
import { FlagsStateContainer } from '@confluence/flags/entry-points/FlagsStateContainer.ts';
import { removeSuperAdminClaim } from '@confluence/super-admin-claim';

import { removeAKTimeout } from '../super-admin-expiration/timeoutUtils.ts';

const i18n = defineMessages({
	disableSuperAdminErrorTitle: {
		id: 'super-admin.disable.super.admin.error.title',
		defaultMessage: 'Something went wrong',
		description: 'Title for the error if disabling Super Admin fails',
	},
	disableSuperAdminErrorDescription: {
		id: 'super-admin.disable.super.admin.error.description-with-link',
		defaultMessage: 'If this keeps happening, let us know at {atlassianSupportLink}',
		description: 'Description for the error if disabling Super Admin fails',
	},
});

export const DisableSuperAdminMutation: FC<any> = ({
	handleMutationCompleteCallback,
	handleMutationErrorCallback,
	children,
}) => {
	const intl = useIntl();

	/**
	 * Removes the frontend claim for Super Admin, and performs a page reload to exit
	 * Super Admin, reset permissions, and provide a good UX.
	 * @returns {function}
	 */
	const handleMutationComplete = (flags) => (result) => {
		const isSuperAdmin = result?.disableSuperAdmin?.user?.confluence?.roles?.isSuperAdmin;

		if (!isSuperAdmin) {
			removeSuperAdminClaim();
			removeAKTimeout();

			handleMutationCompleteCallback();
			window.location.reload();
		} else {
			handleMutationError(flags)();
		}
	};

	/**
	 * Error handling mechanism for network or server errors.
	 * @param {object} flags
	 * @return {function}
	 */
	const handleMutationError = (flags) => () => {
		flags.showErrorFlag({
			title: intl.formatMessage(i18n.disableSuperAdminErrorTitle),
			description: intl.formatMessage(i18n.disableSuperAdminErrorDescription, {
				atlassianSupportLink: <AtlassianSupportLink showHostname />,
			}),
		});

		handleMutationErrorCallback();
	};

	return (
		<Subscribe to={[FlagsStateContainer]}>
			{(flags) => (
				<Mutation
					mutation={graphql`
						mutation DisableSuperAdminMutationComponentMutation {
							disableSuperAdmin {
								user {
									id
									confluence {
										roles {
											isSuperAdmin
										}
									}
								}
							}
						}
					`}
					onCompleted={handleMutationComplete(flags)}
					onError={handleMutationError(flags)}
				>
					{(disableSuperAdminTrigger) => children(disableSuperAdminTrigger, flags)}
				</Mutation>
			)}
		</Subscribe>
	);
};
